import { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog-shadcn";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/modern/components/ui/alert-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import {
  X,
  Image as ImageIcon,
  Sparkles,
  Trash2,
  Pencil,
  Eye,
  ShieldAlert,
  Loader2,
} from "lucide-react";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";
import { EntityRecord } from "@/types/entity";
import { MarketingEditorProvider, useMarketingEditorContext } from "../../context/MarketingEditorContext";
import { EntityFileProvider } from "@/react/components/entityViews/files/context/EntityFileContext";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import {
  AiObservationsCard,
  AiWarningsCard,
} from "@/react/components/entityViews/files/components/editors/FileDetailModal/components/promote/components/AiInsightOverlay";
import { ProblemContextButton } from "@/react/components/entityViews/files/components/editors/FileDetailModal/components/promote/components/ProblemContextDialog";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { DEMOTE_ASSET_CONFIRM_DESCRIPTION } from "../../api/marketingAssets.constants";
import type { SwivuMarketingAsset } from "../../api/marketingAssets.types";
import { ENTITY_RECORD_FIELD_TO_API } from "../../api/marketingAssets.query-keys";
import { resolveMarketingAssetFile } from "../../utils/resolveMarketingAssetFile";


// ---------------------------------------------------------------------------
// Main: grouped card grid
// ---------------------------------------------------------------------------

const AssetCard = ({
  asset,
  isSelected,
  onClick,
}: {
  asset: SwivuMarketingAsset;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const { displayUrl, isLoading } = useFileUrl(asset.file);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative aspect-[4/5] rounded-lg overflow-hidden text-left transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "ring-2 ring-transparent",
        isSelected && "ring-primary",
      )}
    >
      {isLoading ? (
        <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
      ) : displayUrl ? (
        <img
          src={displayUrl}
          alt={asset.altText || asset.caption}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <ImageIcon className="w-10 h-10 text-muted-foreground/20" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-2.5 pb-2.5 pt-10">
        <p className="text-xs text-white/90 font-medium line-clamp-2 leading-snug">
          {asset.caption || "Untitled"}
        </p>
      </div>
    </button>
  );
};

const GroupedAssetGrid = ({
  selectedAssetId,
  onPickAsset,
}: {
  selectedAssetId: number | null;
  onPickAsset: (id: number) => void;
}) => {
  const { assets, temporalGroups, folderGroups } = useMarketingEditorContext();

  if (assets.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center text-muted-foreground">
        <p className="text-sm">No assets yet.</p>
      </div>
    );
  }

  const renderTagGroup = (group: { tag: MarketingTag; assets: SwivuMarketingAsset[] }) => {
    if (group.assets.length === 0) return null;
    return (
      <div key={group.tag.id} className="rounded-md border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 h-[2.75rem] bg-card/50 dark:bg-card border-b border-border/60">
          <h4 className="text-sm font-semibold text-foreground">{group.tag.label}</h4>
          <span className="text-[11px] text-muted-foreground tabular-nums bg-muted/80 rounded-full px-2 py-0.5 font-medium">{group.assets.length}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-3.5">
          {group.assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={asset.id === selectedAssetId}
              onClick={() => onPickAsset(asset.id)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 min-h-0 space-y-6">
      {temporalGroups.length > 0 && (
        <section className="space-y-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
            Temporal Classification
          </span>
          <div className="space-y-3">
            {temporalGroups.map(renderTagGroup)}
          </div>
        </section>
      )}

      {folderGroups.length > 0 && (
        <section className="space-y-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
            Content Roles
          </span>
          <div className="space-y-3">
            {folderGroups.map(renderTagGroup)}
          </div>
        </section>
      )}

      {temporalGroups.length === 0 && folderGroups.length === 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={asset.id === selectedAssetId}
              onClick={() => onPickAsset(asset.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail panel sections
// ---------------------------------------------------------------------------

const DetailSection = ({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) => (
  <div className={cn("space-y-2", className)}>
    <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    {children}
  </div>
);

const TagBadge = ({ label }: { label: string }) => (
  <span className="inline-flex items-center rounded-full border px-2.5 py-1 text-sm font-medium bg-muted/60 text-foreground/80 border-border/30">
    {label}
  </span>
);

const TagGroup = ({ label, tags }: { label: string; tags: MarketingTag[] }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium text-muted-foreground">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => <TagBadge key={t.id} label={t.label} />)}
    </div>
  </div>
);

const AssetReadOnlySummary = ({ asset }: { asset: SwivuMarketingAsset }) => {
  const { getTemporalTag, getContentTags, getProblemTags, getFolderTags } = useMarketingEditorContext();

  const temporal = getTemporalTag(asset);
  const solutionTags = getContentTags(asset);
  const problemTags = getProblemTags(asset);
  const contentTags = getFolderTags(asset);

  const hasTags = temporal || solutionTags.length > 0 || contentTags.length > 0;

  return (
    <div className="divide-y divide-border/50">
      <div className="py-3.5 first:pt-0">
        <DetailSection label="Caption">
          <p className="text-sm text-foreground leading-relaxed">{asset.caption || "—"}</p>
        </DetailSection>
      </div>

      <div className="py-3.5">
        <DetailSection label="Alt text">
          <p className="text-sm text-muted-foreground leading-relaxed">{asset.altText || "—"}</p>
        </DetailSection>
      </div>

      {hasTags && (
        <div className="py-3.5">
          <DetailSection label="Tags">
            <div className="space-y-3">
              {temporal && (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-muted-foreground">Phase</p>
                  <TagBadge label={temporal.label} />
                </div>
              )}
              {solutionTags.length > 0 && <TagGroup label="Solution" tags={solutionTags} />}
              {contentTags.length > 0 && <TagGroup label="Content Role" tags={contentTags} />}
            </div>
          </DetailSection>
        </div>
      )}

      {problemTags.length > 0 && (
        <div className="py-1.5">
          <ProblemContextButton tags={problemTags} />
        </div>
      )}

      <div className="py-3.5">
        <DetailSection label="Restriction">
          <div className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium border",
            asset.isRestricted
              ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
              : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
          )}>
            {asset.isRestricted ? (
              <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <Eye className="w-3.5 h-3.5 shrink-0" />
            )}
            {asset.isRestricted ? "Restricted" : "Public Eligible"}
          </div>
          {asset.isRestricted && asset.restrictionReason && (
            <p className="text-sm text-muted-foreground mt-1">{asset.restrictionReason}</p>
          )}
        </DetailSection>
      </div>
    </div>
  );
};

const AiAnalysisSection = ({ asset }: { asset: SwivuMarketingAsset }) => {
  const observations: string[] = asset.aiObservations ?? [];
  const warnings: string[] = asset.visibilityWarnings ?? [];

  if (observations.length === 0 && warnings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-muted-foreground border border-dashed border-border/60 rounded-lg">
        <Sparkles className="w-5 h-5 mb-1.5 opacity-20" />
        <p className="text-sm">No AI analysis available</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <AiObservationsCard items={observations} />
      <AiWarningsCard items={warnings} delay={observations.length > 0 ? 0.1 : 0} />
    </div>
  );
};

const SlidingDetailPanel = ({
  isOpen,
  onClose,
  onOpenFileDetail,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpenFileDetail: (asset: SwivuMarketingAsset) => void;
}) => {
  const { selectedAsset, deleteAsset, demoting } = useMarketingEditorContext();
  const [confirmDemote, setConfirmDemote] = useState(false);

  const { displayUrl: previewUrl, isLoading: isUrlLoading } = useFileUrl(selectedAsset?.file ?? null);

  const handleConfirmDemote = async () => {
    if (!selectedAsset || demoting) return;
    try {
      await deleteAsset(selectedAsset);
      setConfirmDemote(false);
    } catch {
      // Error toast handled in deleteAsset
    }
  };

  return (
    <AnimatePresence>
      {isOpen && selectedAsset && (
        <motion.div
          key="marketing-asset-detail"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: "38%", opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="border-l border-border overflow-hidden shrink-0 min-w-0 flex flex-col min-h-0"
        >
          <div className="flex flex-col h-full min-h-0 w-full min-w-0">
            <div className="flex items-center justify-between gap-2 px-4 h-11 border-b border-border/60 shrink-0 relative z-10">
              <span className="text-[13px] font-semibold text-foreground truncate">Details</span>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
                <X className="w-3.5 h-3.5" />
                <span className="sr-only">Close panel</span>
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="w-full min-h-[140px] max-h-[35vh] bg-black/40 overflow-hidden">
                {isUrlLoading ? (
                  <Skeleton className="w-full min-h-[140px] max-h-[35vh] rounded-none" />
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={selectedAsset.altText || selectedAsset.caption}
                    className="w-full h-full max-h-[35vh] object-contain"
                  />
                ) : (
                  <div className="w-full min-h-[140px] flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-muted-foreground/15" />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b border-border/40">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs flex-1"
                  onClick={() => onOpenFileDetail(selectedAsset)}
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 flex-1"
                  disabled={demoting}
                  onClick={() => setConfirmDemote(true)}
                >
                  {demoting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  {demoting ? "Demoting…" : "Demote"}
                </Button>
              </div>

              <AlertDialog open={confirmDemote} onOpenChange={(open) => !demoting && setConfirmDemote(open)}>
                <AlertDialogContent className="z-[--swivu-bryntum-z-index]">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Demote Asset</AlertDialogTitle>
                    <AlertDialogDescription>
                      {DEMOTE_ASSET_CONFIRM_DESCRIPTION}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={demoting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={demoting}
                      onClick={(e) => {
                        e.preventDefault();
                        void handleConfirmDemote();
                      }}
                    >
                      {demoting ? "Demoting…" : "Demote"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <div className="px-3.5 divide-y divide-border/50">
                <div className="py-3.5">
                  <AssetReadOnlySummary asset={selectedAsset} />
                </div>

                <div className="py-3.5">
                  <DetailSection label="AI Analysis">
                    <AiAnalysisSection asset={selectedAsset} />
                  </DetailSection>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// Main editor content
// ---------------------------------------------------------------------------

const MarketingAssetEditorContent = () => {
  const { onCloseEditor, entityRecord, selectedAssetId, selectAsset, assets } = useMarketingEditorContext();
  const { selectedBoard } = useSwivuBryntumContext();
  const [isOpen, setIsOpen] = useState(true);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);

  const [fileDetailFile, setFileDetailFile] = useState<SwivuFile | null>(null);
  const [isFileDetailOpen, setIsFileDetailOpen] = useState(false);

  useEffect(() => {
    if (selectedAssetId == null) {
      setIsDetailPanelOpen(false);
    }
  }, [selectedAssetId]);

  useEffect(() => {
    if (assets.length > 0 && selectedAssetId == null) {
      selectAsset(assets[0].id);
      setIsDetailPanelOpen(true);
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onCloseEditor();
  }, [onCloseEditor]);

  const handlePickAsset = useCallback(
    (id: number) => {
      selectAsset(id);
      setIsDetailPanelOpen(true);
    },
    [selectAsset],
  );

  const handleOpenFileDetail = useCallback(
    async (asset: SwivuMarketingAsset) => {
      const swivuFile = await resolveMarketingAssetFile({
        boardId: selectedBoard?.id,
        entityRecord,
        fileId: asset.file,
      });
      if (!swivuFile) return;
      setFileDetailFile(swivuFile);
      setIsFileDetailOpen(true);
    },
    [entityRecord, selectedBoard?.id],
  );

  return (
    <>
      <Dialog
        open={isOpen}
        modal={!isFileDetailOpen}
        onOpenChange={(open) => {
          // Keep the marketing editor mounted while FileDetailModal is open;
          // that modal lives in .b-float-root (z-index 14000) and would sit
          // behind this dialog (z-index 140001) if we closed via onOpenChange.
          if (!open && !isFileDetailOpen) handleClose();
        }}
      >
        <DialogContent
          showCloseButton={false}
          className={cn(
            "z-[--swivu-bryntum-z-index] sm:max-w-7xl w-[min(100vw-2rem,80rem)] h-[85dvh] flex flex-col p-0 gap-0 focus-visible:outline-none rounded-lg",
            // FileDetailModal portals into .b-float-root (z 14000); this dialog
            // is 140001, so hide it while file detail is open or Edit looks dead.
            isFileDetailOpen && "invisible pointer-events-none",
          )}
          onInteractOutside={(e) => {
            if (isFileDetailOpen) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isFileDetailOpen) e.preventDefault();
          }}
        >
          <div className="flex items-center justify-between p-4 border-b border-border gap-2 min-w-0 flex-none relative z-10">
            <h2 className="text-xl text-muted-foreground">
              Marketing Assets
              <span className="text-muted-foreground/60 font-normal mx-1.5">|</span>
              <span className="text-muted-foreground font-normal">{entityRecord.name}</span>
            </h2>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">{assets.length} promoted</span>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handleClose}>
                <X className="w-4 h-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <div className="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden">
              <GroupedAssetGrid selectedAssetId={selectedAssetId} onPickAsset={handlePickAsset} />
            </div>

            <SlidingDetailPanel
              isOpen={isDetailPanelOpen}
              onClose={() => { setIsDetailPanelOpen(false); selectAsset(null!); }}
              onOpenFileDetail={handleOpenFileDetail}
            />
          </div>
        </DialogContent>
      </Dialog>

      {fileDetailFile && (
        <EntityFileProvider
          record={entityRecord}
          entityType={ENTITY_RECORD_FIELD_TO_API[entityRecord.entityType] ?? "task"}
        >
          <FileDetailModal
            entityRecord={entityRecord}
            file={fileDetailFile}
            open={isFileDetailOpen}
            setOpen={setIsFileDetailOpen}
            autoOpenPromotePanel
          />
        </EntityFileProvider>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Public export (matches EditorViews prop contract)
// ---------------------------------------------------------------------------

interface SwivuMarketingAssetEditorProps {
  record: { entityRecord: EntityRecord };
  onClose: () => void;
  editorInstance?: unknown;
}

export const SwivuMarketingAssetEditor = ({ record, onClose }: SwivuMarketingAssetEditorProps) => (
  <MarketingEditorProvider entityRecord={record.entityRecord} onCloseEditor={onClose}>
    <MarketingAssetEditorContent />
  </MarketingEditorProvider>
);
